/**
 * Confetti — subtle particle burst for right-swipe (Setuju) feedback.
 *
 * Phase 3.4 polish (per plan §7.1 task #13 + §7.6 risks):
 *  - 12-16 small colored squares falling from top of the card area
 *  - 500ms total, then auto-dismiss
 *  - Random X drift + random rotation
 *  - Respects `prefers-reduced-motion`: in that case, no animation,
 *    particle just fades in then out
 *  - Imperative API: parent calls `play()` to trigger
 *
 * Implementation: React Native Animated API (not Reanimated) for
 * simpler per-particle state. Each particle is its own Animated.Value
 * pair (translateX, translateY, rotate, opacity). 12 particles × 4
 * values × 500ms = cheap.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, StyleSheet, View } from 'react-native';

export type ConfettiHandle = {
  /** Trigger a 500ms confetti burst from the top of the parent area. */
  play: () => void;
};

const PARTICLE_COUNT = 14;
const ANIM_DURATION_MS = 500;
const FALL_DISTANCE = 320; // px from top of container

// Studio Dini Hari palette + warm accent
const COLORS = [
  '#563F7A', // primary purple
  '#0A7473', // teal (Biar Kami Pilih)
  '#FF8A3D', // accent orange
  '#7FD1B9', // hero mint
  '#FFE5D4', // variety soft orange
  '#D4E5FF', // novelty soft blue
  '#F0E4FF', // wildcard soft purple
  '#4caf50', // approve green
];

type ParticleState = {
  startX: number; // 0-1 fraction of container width
  endX: number;   // 0-1 fraction
  rotate: number; // degrees
  color: string;
  delay: number;  // ms
  translateX: Animated.Value;
  translateY: Animated.Value;
  rotateAnim: Animated.Value;
  opacity: Animated.Value;
};

function makeParticles(): ParticleState[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const startX = 0.2 + Math.random() * 0.6;
    const endX = startX + (Math.random() - 0.5) * 0.5;
    const rotate = (Math.random() - 0.5) * 720;
    return {
      startX,
      endX,
      rotate,
      color: COLORS[i % COLORS.length] ?? '#563F7A',
      delay: Math.floor(Math.random() * 100),
      translateX: new Animated.Value(0),
      translateY: new Animated.Value(0),
      rotateAnim: new Animated.Value(0),
      opacity: new Animated.Value(0),
    };
  });
}

export const Confetti = forwardRef<ConfettiHandle>(function Confetti(_, ref) {
  const particles = useRef<ParticleState[]>(makeParticles()).current;
  // Track in-flight animations so we can stop on unmount.
  const running = useRef<Animated.CompositeAnimation[]>([]);
  // Track prefers-reduced-motion. When true, play() is a no-op.
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled().then((enabled) => {
      if (mounted) setReduceMotion(enabled);
    }).catch(() => {
      // If the API isn't available (e.g. older RN), assume motion OK.
    });
    const sub = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      (enabled: boolean) => {
        if (mounted) setReduceMotion(enabled);
      },
    );
    return () => {
      mounted = false;
      sub.remove();
      for (const animation of running.current) animation.stop();
      running.current = [];
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        // Reduced motion: skip the burst entirely. The haptic + toast
        // still fire, so the user gets feedback without the animation.
        if (reduceMotion) return;

        for (const animation of running.current) animation.stop();
        running.current = [];

        // Reset all particles to start state.
        particles.forEach((p) => {
          p.translateX.setValue(0);
          p.translateY.setValue(0);
          p.rotateAnim.setValue(0);
          p.opacity.setValue(0);
        });

        const anims = particles.map((p) => {
          const anim = Animated.parallel([
            Animated.timing(p.translateX, {
              toValue: (p.endX - p.startX) * 320, // px
              duration: ANIM_DURATION_MS,
              easing: Easing.out(Easing.quad),
              useNativeDriver: true,
            }),
            Animated.timing(p.translateY, {
              toValue: FALL_DISTANCE,
              duration: ANIM_DURATION_MS,
              easing: Easing.in(Easing.quad),
              useNativeDriver: true,
            }),
            Animated.timing(p.rotateAnim, {
              toValue: p.rotate,
              duration: ANIM_DURATION_MS,
              easing: Easing.linear,
              useNativeDriver: true,
            }),
            Animated.sequence([
              Animated.timing(p.opacity, {
                toValue: 1,
                duration: 80,
                useNativeDriver: true,
              }),
              Animated.delay(ANIM_DURATION_MS - 200),
              Animated.timing(p.opacity, {
                toValue: 0,
                duration: 120,
                useNativeDriver: true,
              }),
            ]),
          ]);
          // Stagger start so the burst looks organic, not all-at-once.
          return Animated.sequence([Animated.delay(p.delay), anim]);
        });
        const composite = Animated.stagger(20, anims);
        running.current.push(composite);
        composite.start(({ finished }) => {
          running.current = running.current.filter((animation) => animation !== composite);
          if (finished) {
            // Reset opacity to 0 for next play.
            particles.forEach((p) => p.opacity.setValue(0));
          }
        });
      },
    }),
    [particles, reduceMotion],
  );

  return (
    <View pointerEvents="none" style={styles.container}>
      {particles.map((p, i) => (
        <Animated.View
          key={i}
          style={[
            styles.particle,
            {
              left: `${p.startX * 100}%`,
              backgroundColor: p.color,
              transform: [
                { translateX: p.translateX },
                { translateY: p.translateY },
                { rotate: p.rotateAnim.interpolate({
                  inputRange: [0, 360],
                  outputRange: ['0deg', '360deg'],
                }) },
              ],
              opacity: p.opacity,
            },
          ]}
        />
      ))}
    </View>
  );
});

const PARTICLE_SIZE = 8;

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: FALL_DISTANCE + 40,
    overflow: 'visible',
  },
  particle: {
    position: 'absolute',
    top: 0,
    width: PARTICLE_SIZE,
    height: PARTICLE_SIZE * 1.6,
    borderRadius: 2,
  },
});
